import JsBarcode from 'jsbarcode';

export interface PreparedFedExZpl {
  zpl: string;
  pageCount: number;
  // Per page, whether ^POI rotates it 180°.
  inverted: boolean[];
}

interface BarcodeEncoding {
  data: string;
}

interface BarcodeTarget {
  encodings?: BarcodeEncoding[];
}

interface CodabarSpec {
  height: number;
  start: string;
  stop: string;
}

// The existing FedEx request uses STOCK_4X6, not the Deleo packing sheet's 150 × 100 mm.
export const FEDEX_LABEL_WIDTH_MM = 101.6;
export const FEDEX_LABEL_HEIGHT_MM = 152.4;
export const FEDEX_LABEL_DPMM = 8;
// Field calibration: moves each label 13 dots (~1.6 mm) toward its left edge so the right edge is not clipped.
export const FEDEX_PRINT_SHIFT_LEFT_DOTS = 13;
const MAX_ZPL_BYTES = 128 * 1024;
const CARRIER_PAGE_COUNT = 2;
const MAX_DOTS_X = Math.floor(FEDEX_LABEL_WIDTH_MM * FEDEX_LABEL_DPMM);
const MAX_DOTS_Y = Math.floor(FEDEX_LABEL_HEIGHT_MM * FEDEX_LABEL_DPMM);

// Never rely on Zebrash silently ignoring a new, potentially carrier-critical command.
// PR/MD configure the physical printer; PQ is accepted only for one copy.
const SUPPORTED_COMMANDS = new Set([
  '^XA',
  '^XZ',
  '^PW',
  '^LH',
  '^PO',
  '^PQ',
  '^PR',
  '^MD',
  '^CI',
  '^CF',
  '^A0',
  '^FO',
  '^FW',
  '^FB',
  '^FD',
  '^FH',
  '^FS',
  '^GB',
  '^BY',
  '^BC',
  '^B7',
  '^BK',
  '^Ad',
  '^Ab',
]);

const fail = (): never => {
  // Do not echo label contents (names, addresses, tracking payloads) into errors or logs.
  throw new Error(
    'This FedEx label contains an unsupported or invalid ZPL format. Printing was stopped to preserve the carrier label.',
  );
};

const integer = (value: string | undefined, min: number, max: number): number => {
  if (!value || !/^\d+$/.test(value)) {
    return fail();
  }
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < min || parsed > max) {
    return fail();
  }
  return parsed;
};

const codabarSpec = (parameters: string): CodabarSpec => {
  const parts = parameters.split(',');
  const [orientation, checksum, height, interpretation, above, start, stop] = parts;
  // These are the exact semantics used by the archived FedEx labels. Other modes must
  // be implemented and verified before use, not approximated as an ordinary barcode.
  if (parts.length !== 7 || orientation !== 'N' || checksum !== 'N' || interpretation !== 'N' || above !== 'N') {
    return fail();
  }
  if (!start || !stop || !/^[A-D]$/.test(start) || !/^[A-D]$/.test(stop)) {
    return fail();
  }
  return { height: integer(height, 1, MAX_DOTS_Y), start, stop };
};

const codabarGraphic = (data: string, spec: CodabarSpec, narrow: number, ratio: number, maxWidth: number): string => {
  // FedEx includes the same guards in ^FD as well as ^BK. Encode them once only;
  // conflicting guards are not safe to reinterpret.
  const payload = data.startsWith(spec.start) && data.endsWith(spec.stop) ? data.slice(1, -1) : data;
  if (!/^[0-9\-$:.+/]{1,80}$/.test(payload)) {
    return fail();
  }
  const target: BarcodeTarget = {};
  // Use the existing encoder, including explicit carrier-specified start/stop characters.
  JsBarcode(target, spec.start + payload + spec.stop, { format: 'codabar', displayValue: false });
  const encoded = target.encodings?.map((encoding) => encoding.data).join('');
  if (!encoded || !/^[01]+$/.test(encoded)) {
    return fail();
  }
  // JsBarcode encodes narrow/wide elements as one/two bits. ZPL ^BY sets their physical
  // widths independently; scaling the complete barcode would lose the carrier's ratio.
  const runs = encoded.match(/0+|1+/g) ?? [];
  const bits = runs
    .map((run) => {
      if (run.length !== 1 && run.length !== 2) {
        return fail();
      }
      return run[0]!.repeat(run.length === 1 ? narrow : Math.round(narrow * ratio));
    })
    .join('');
  if (bits.length > maxWidth) {
    return fail();
  }
  const bytesPerRow = Math.ceil(bits.length / 8);
  const padded = bits.padEnd(bytesPerRow * 8, '0');
  let row = '';
  for (let offset = 0; offset < padded.length; offset += 8) {
    row += Number.parseInt(padded.slice(offset, offset + 8), 2)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  }
  const totalBytes = bytesPerRow * spec.height;
  // A lossless, dot-for-dot graphic in the same field; no carrier identity/layout changes.
  return `^GFA,${totalBytes},${totalBytes},${bytesPerRow},${row.repeat(spec.height)}`;
};

export const prepareFedExZpl = (zpl: string): PreparedFedExZpl => {
  if (!zpl || zpl.length > MAX_ZPL_BYTES || !zpl.trimStart().startsWith('^XA')) {
    return fail();
  }
  const tokens = [...zpl.matchAll(/([~^][A-Z][A-Za-z0-9])([^~^]*)/g)];
  if (tokens.map((token) => token[0]).join('') !== zpl.trimStart()) {
    return fail();
  }
  let inPage = false;
  let pageCount = 0;
  let fieldOrientation = 'N';
  let pending: CodabarSpec | undefined;
  let converted = false;
  let hexIndicator: string | undefined;
  let narrow = 2;
  let ratio = 3;
  let originX = 0;
  let originY = 0;
  let homeX = 0;
  let homeY = 0;
  let printWidth = MAX_DOTS_X;
  const output: string[] = [];
  const inverted: boolean[] = [];

  for (const token of tokens) {
    const command = token[1]!;
    const raw = token[2]!;
    const value = raw.trim();
    if (!SUPPORTED_COMMANDS.has(command)) {
      return fail();
    }
    if (command === '^XA') {
      if (inPage || value || ++pageCount > CARRIER_PAGE_COUNT) {
        return fail();
      }
      inPage = true;
      fieldOrientation = 'N';
      narrow = 2;
      ratio = 3;
      originX = originY = homeX = homeY = 0;
      printWidth = MAX_DOTS_X;
      inverted.push(false);
    } else if (!inPage) {
      return fail();
    }
    if (command === '^XZ') {
      if (pending || value) {
        return fail();
      }
      inPage = false;
    }
    if (command === '^PQ' && value !== '1') {
      return fail();
    }
    if (command === '^PW') {
      printWidth = integer(value, 1, MAX_DOTS_X);
    }
    if (command === '^PO') {
      if (value !== 'I' && value !== 'N') {
        return fail();
      }
      inverted[inverted.length - 1] = value === 'I';
    }
    if (command === '^CI' && value !== '13') {
      return fail();
    }
    if (command === '^LH' || command === '^FO') {
      const coords = value.split(',');
      if (coords.length !== 2) {
        return fail();
      }
      const x = integer(coords[0], 0, MAX_DOTS_X);
      const y = integer(coords[1], 0, MAX_DOTS_Y);
      if (command === '^LH') {
        homeX = x;
        homeY = y;
      } else {
        originX = x;
        originY = y;
      }
    }
    if (command === '^FW') {
      fieldOrientation = value;
    }
    if (command === '^BY') {
      const widths = value.split(',');
      narrow = integer(widths[0], 1, 10);
      ratio = widths[1] ? Number(widths[1]) : 3;
      if (!Number.isFinite(ratio) || ratio < 2 || ratio > 3 || widths.length > 3) {
        return fail();
      }
      if (widths[2]) {
        integer(widths[2], 1, MAX_DOTS_Y);
      }
    }
    if (command === '^BK') {
      if (pending) {
        return fail();
      }
      pending = codabarSpec(value);
      converted = false;
      continue;
    }
    if (command === '^FH') {
      if (value.length > 1) {
        return fail();
      }
      hexIndicator = value || '_';
    }
    if (command === '^FD' && pending) {
      if (converted || fieldOrientation !== 'N' || originY + homeY + pending.height > MAX_DOTS_Y) {
        return fail();
      }
      const data = raw.replace(/[\r\n]/g, '');
      let decoded = '';
      for (let index = 0; index < data.length; index++) {
        if (hexIndicator && data[index] === hexIndicator) {
          const pair = data.slice(index + 1, index + 3);
          if (!/^[0-9A-Fa-f]{2}$/.test(pair)) {
            return fail();
          }
          decoded += String.fromCharCode(Number.parseInt(pair, 16));
          index += 2;
        } else {
          decoded += data[index];
        }
      }
      output.push(codabarGraphic(decoded, pending, narrow, ratio, printWidth - originX - homeX));
      converted = true;
      continue;
    }
    if (command === '^FS') {
      if (pending && !converted) {
        return fail();
      }
      pending = undefined;
      converted = false;
      hexIndicator = undefined;
    }
    output.push(token[0]);
  }
  if (inPage || pageCount !== CARRIER_PAGE_COUNT || pending) {
    return fail();
  }
  return { zpl: output.join(''), pageCount, inverted };
};
